import { createFileRoute } from "@tanstack/react-router";
import { InboxPage, useInboxCategories } from "@/client/components/inbox-page";
import { Toolbar } from "@/client/components/page";
import { HeldCount, PagePending } from "@/client/components/page-pending";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { PAGE_LOADED } from "@/client/lib/query-client";
import { ensureTenantData, fetchTenantData } from "@/client/lib/session";
import { tasksQuery } from "@/client/lib/tasks-query";
import {
  INBOX_PAGE,
  InboxHeading,
  InboxShowMenu,
  InboxTabs,
} from "@/features/inbox/components/inbox-list";
import { inboxQuery } from "@/features/inbox/inbox-query";
import { type InboxSearch, inboxSearch } from "@/features/inbox/schemas";
import { leaveTypesQuery } from "@/features/leave/leave-query";
import { peopleQuery } from "@/features/people/people-query";

export const Route = createFileRoute("/_authenticated/$slug/inbox")({
  validateSearch: inboxSearch,
  pendingMs: PENDING_MS,
  loader: async ({ context }) => {
    const leaveOn = context.companyModules.includes("leave");
    await Promise.all([
      // A copy saved before this page may miss what arrived since, and would read as caught up.
      fetchTenantData(context.queryClient, {
        ...inboxQuery,
        staleTime: (query) =>
          query.state.dataUpdatedAt >= PAGE_LOADED
            ? Number.POSITIVE_INFINITY
            : 0,
      }),
      ensureTenantData(context.queryClient, peopleQuery),
      ensureTenantData(context.queryClient, tasksQuery),
      leaveOn ? ensureTenantData(context.queryClient, leaveTypesQuery) : null,
    ]);
  },
  head: async ({ match }) => {
    const { inboxCopy } = await import("@/features/inbox/copy");
    return {
      meta: [{ title: pageTitle(inboxCopy.title, match.context.companyName) }],
    };
  },
  pendingComponent: InboxPending,
  component: Inbox,
});

/** The tabs and the Show menu for real; only Unread's count waits on the inbox. */
function InboxPending() {
  const search = Route.useSearch();
  const onSearch = useSearchChange();
  return (
    <PagePending
      shape="list"
      className={INBOX_PAGE}
      header={
        <>
          <InboxHeading />
          <Toolbar
            views={
              <InboxTabs
                search={search}
                onSearch={onSearch}
                unread={<HeldCount />}
              />
            }
            inline
            actions={
              <InboxShowMenu
                categories={useInboxCategories()}
                search={search}
                onSearch={onSearch}
              />
            }
          />
        </>
      }
    />
  );
}

/** Replaced so Back leaves the inbox instead of stepping through every row opened. */
function useSearchChange() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  return (change: Partial<InboxSearch>) =>
    navigate({ search: { ...search, ...change }, replace: true });
}

function Inbox() {
  const search = Route.useSearch();
  return <InboxPage search={search} onSearch={useSearchChange()} />;
}
